import { useCallback, useMemo } from 'react';
import {
  actionsDone,
  actionsMenuClick,
  actionsOpenClick,
  mediaManagerPanelFileInfoCopyUrlClick,
} from '@wix/bi-logger-media-manager-g6/v2';

import { ActionName, BIPageName } from '../../../../constants/Bi';
import { bi } from '../../../../services/loggers/bi';
import { extractCommonBiFieldsFromItems } from '../../../bi';
import { ActionClickOrigin, ActionTarget, IWithTrackActionsInner } from './useTrackActions.types';
import { MediaManagerSDKAnyItem, MediaManagerSDKFile } from '../../../media-manager-sdk';
import { isBoard } from '../../../media-manager-sdk/boards';
import { isFile } from '../../../media-manager-sdk/items';
import { getExtension } from '../../../path';
import { useFileType } from '../useFileType';

const calculateParams = (selectedItems: MediaManagerSDKAnyItem[], target: ActionTarget = null, fileType) => {
  const firstItem = selectedItems[0];
  const areMultipleItemsSelected = selectedItems.length > 1;

  const calculatedTarget = areMultipleItemsSelected
    ? ActionTarget.Batch
    : isFile(firstItem)
    ? ActionTarget.File
    : isBoard(firstItem)
    ? ActionTarget.Board
    : ActionTarget.Folder;

  return {
    target: target || calculatedTarget,
    ...extractCommonBiFieldsFromItems(selectedItems, { fileType }),
  };
};

let lastActionOpenClickOrigin: ActionClickOrigin = null;
let lastActionTarget: ActionTarget = null;
let shouldPreserveOriginAndTargetAfterDone: boolean = false;

const preserveOriginAndTargetAfterDone = () => {
  shouldPreserveOriginAndTargetAfterDone = true;
};

const clearOriginAndTarget = () => {
  shouldPreserveOriginAndTargetAfterDone = false;
  lastActionOpenClickOrigin = null;
  lastActionTarget = null;
};

export const useTrackActions = () => {
  const fileType = useFileType();

  const setLastActionParams = useCallback<IWithTrackActionsInner['setLastActionParams']>(
    (selectedItems, { clickOrigin, target = null }) => {
      const { target: calculatedTarget } = calculateParams(selectedItems, target, fileType);

      lastActionOpenClickOrigin = clickOrigin;
      lastActionTarget = calculatedTarget;
    },
    [fileType],
  );

  return useMemo(
    () => ({
      trackActionsOpenClick: (selectedItems, clickOrigin, target?) => {
        const params = {
          clickOrigin,
          ...calculateParams(selectedItems, target, fileType),
        };

        bi.logger().report(actionsOpenClick(params));
      },
      trackActionsMenuClick: (selectedItems, clickOrigin, actionName, target?) => {
        const params = {
          actionName,
          clickOrigin,
          pageName: BIPageName.MediaManager,
          ...calculateParams(selectedItems, target, fileType),
        };

        bi.logger().report(actionsMenuClick(params));

        setLastActionParams(selectedItems, params);
      },
      trackActionsDownload: (selectedItems) => {
        const params = {
          actionName: ActionName.Download,
          clickOrigin: ActionClickOrigin.InfoPanel,
          ...calculateParams(selectedItems, lastActionTarget, fileType),
        };

        bi.logger().report(actionsDone(params));
      },
      trackActionsDone: (selectedItems, actionName) => {
        setTimeout(() => {
          if (!lastActionOpenClickOrigin || !lastActionTarget) {
            return;
          }

          const params = {
            actionName,
            clickOrigin: lastActionOpenClickOrigin,
            pageName: BIPageName.MediaManager,
            ...calculateParams(selectedItems, lastActionTarget, fileType),
          };

          bi.logger().report(actionsDone(params));

          if (!shouldPreserveOriginAndTargetAfterDone) {
            clearOriginAndTarget();
          }
        }, 0);
      },
      trackManagerPanelFileInfoCopyUrlClick: (item: MediaManagerSDKFile) => {
        bi.logger().report(
          mediaManagerPanelFileInfoCopyUrlClick({
            actionName: ActionName.CopyUrl,
            clickOrigin: ActionTarget.Panel,
            fileFormat: getExtension(item.name),
            fileId: item.id,
            mediaType: item.mediaType,
          }),
        );
      },
      setLastActionParams,
      preserveOriginAndTargetAfterDone,
      clearOriginAndTarget,
    }),
    [fileType],
  );
};
